import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { TenantsService } from './../services/tenants.service';
import { Tenant } from './../config.types';

// class for each display item 
// -> should be outsourced
class DisplayData {
    level:    number;
    nodeName: string;
    apiName:  string;
    path:     string;
    value:    any;
    oldValue: any;
    changed:  boolean;
    inserted: boolean;
    deleted:  boolean;
    valid:    boolean;
    hasChildren: boolean;
    childs:   DisplayData[];
}
// exception for validation error, path defines the excepted field
// -> should be outsourced
class DataValidationError extends Error {
    public path: string;
    constructor(path: string, msg: string){ super(msg); this.path=path; }
}

@Component({
    selector: 'b4a-admin-tenant',
    templateUrl: './tenant.component.html',
    styleUrls: ['./tenant.component.css']
})
export class TenantComponent implements OnInit {
    id:      number; // tenant id
    tenant:  Tenant; // tenant data, returned from api-call
    isAdmin: Boolean; // tenant is admin -> no login as available
    data:    DisplayData[]; // tenant data, prepared for display and change
    changed: Boolean = false; // flag for recognize changes, toggles button state
    insertCounter: number = 0; // internal counter, used for new hostnames
    errMsg:  String = null;    // if not null, error message will be displayed

    constructor(
        private service:  TenantsService,
        private router:   Router,
        private route:    ActivatedRoute,
        private location: Location
    ){}
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * locate tenant id for edit data, if null or not a number display form to insert new tenant
     */
    ngOnInit(): void{
        this.route.params.subscribe(
            (params) => { this.id = +params['id']; if(isNaN(this.id)) this.id=null; this.loadData(); },
            (error) => { this.displayError(error); }
        );
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * analyse error and check for display message
     * @param err 
     */
    displayError(err): void {
        // console output for debug
        console.error(err);
    
        let msg = null;
        // data validiation error:
        if(err instanceof DataValidationError){ msg = err.message; }
        // identify xhr error (eg api zend exception)
        else if(typeof(err)==='object'&&err.status){ msg = this.parseXhrError(err); }
        // regular javascript error:
        else if(err instanceof Error){ msg = err.message; }
        // unknown error
        if(msg===null||msg==='') { msg = 'Unerwartete Ausnahme aufgetreten. Bitte kontaktieren Sie Ihren Systemadministrator.'; }
        // display error message
        this.errMsg = msg;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * navigate back to tenants list
     */
    goBack(): void {
        this.router.navigate(['/tenants']);
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * requests tenent details if id is given, else display form for new tenant
     */
    loadData(): void {
        // a) new tenant:
        if(this.id===null){ this.newTenant(); }
        // b) existing tenant:
        else {
            this.service.loadTenant(this.id).subscribe(
                // success:
                (tenant: Tenant) => { 
                    this.tenant = tenant; 
                    this.isAdmin = tenant.admin;
                    this.prepareDisplayData(tenant); },
                // error
                (error) => { this.displayError(error); }
            );
        }
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * config mask for insert new tenant
     */
    newTenant(): void {
        // reset id
        this.id = null;
        this.isAdmin = false;
        // ini "tenant" data with default values
        let data = {
            id:         null,
            admin:      false,
            sales:      false,
            dev:        false,
            isArchived: false,
            code:       '',
            name:       ''
        };
        this.tenant = null;
        // prepare data for display output
        this.prepareDisplayData(data);
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * resets changes, (re-)initialize with responsed data
     */
    cancel(): void {
        this.prepareDisplayData(this.tenant);
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * exec validiation and submit changes when user clicks "Speichern"
     */
    save(): void {
        // exec validation
        try { this.validateInsertData(); } catch(err) { this.displayError(err); return ; }
        // start exec save loop
        this.execSaveLoop();
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * prepare tenent data from api response for display and changing
     * @param data 
     */
    prepareDisplayData(data: any): void {
        // initialize fields
        this.data = []; this.changed = false; this.errMsg = null;
        // start recurse preparing
        this.prepareDisplayDataRecurse(data, this.data, 0, '');
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * recurse preparing display data, steps down in params hierarchy responsed from api
     * @param src 
     * @param dst 
     * @param lvl 
     * @param path 
     */
    prepareDisplayDataRecurse(src: Object, dst: object[], lvl: number, path: string): void {
        //console.log('TenantComponent.prepareDisplayDataRecurse src:%o dst:%o lvl:%o path:%o',src,dst,lvl,path);
        for(let n in src){
            let p = new DisplayData();
            p.level = lvl;
            p.apiName = n; p.nodeName = n;
            p.path = path + n;
            p.changed = false; p.inserted = false; p.deleted = false; p.valid = null;
            p.hasChildren = (typeof(src[n])==='object');
            if(p.hasChildren){
                p.childs = []; this.prepareDisplayDataRecurse(src[n], p.childs, lvl+1, path+n+'.');
            }
            else { 
                p.value = src[n]; p.oldValue = p.value; 
            }
            dst.push(p);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * search for given item specified per path in given data-storage and returns index in array, if not found -1
     * @param path 
     * @param items 
     */
    indexOf(path: String, items: DisplayData[]): number {
        var Idx = -1;
        for(let i=0; i<items.length; ++i)
            if(items[i].nodeName===path){ Idx=i; break; }
        return Idx;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * search for given item specified per path, returns null if not found
     * @param path 
     */
    getItem(path: String): DisplayData {
        return this.getItemRecurse(path, this.data);
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * search for given item specified per path recurse, returns null if not found
     * @param path 
     * @param items 
     */
    getItemRecurse(path: String, items: DisplayData[]): DisplayData {
        let data: DisplayData = null;
        // check for item in items-array, if not found reduce path recursive by removing '.%'
        let p=path, n='';
        while(data===null){
            let idx = this.indexOf(p, items);
            // if item found: use for result
            if(idx>-1){ data=items[idx]; }
            // if item not found: check if '.' in path and reduce by removing '.%'
            else {
                let x = p.lastIndexOf('.');
                if(x>-1){ n = p.substr(x+1,p.length) + ((n!=='')?'.':'') + n; p = p.substr(0,x); }
                // exit-condition
                else { break; }
            }
        }
        // if  next-path exists: repeat search recursive
        if(data!==null && n!==''){ data = this.getItemRecurse(n, data.childs); }
        //console.log('getItemRecurse path:%o items:%o p:%o n:%o data:%o',path,items,p,n,data);
        return data;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /** returns value from field, specified per path */
    getValue(path: String): any {
        let data: DisplayData = this.getItem(path);
        return (data!==null) ? data.value : null;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /** sets value to field, specified per path */
    setValue(path: String, value: any): void {
        let data: DisplayData = this.getItem(path);
        data.value = value; data.changed = true; data.valid = null; this.changed = true; 
    }
    //-----------------------------------------------------------------------------------------------------------------
    /** change key for field, only for new hostnames! */
    setKey(path: String, key: any): void {
        let data: DisplayData = this.getItem(path);
        data.apiName = key; data.changed = true; this.changed = true;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * add new hostname fields to data storage and display output
     */
    addHostname(): void {
        // check for existing new hostname storage (=marked with leading ?) and initiate if necessary
        let hostnames = this.getItem('hostnames?');
        if(hostnames===null){
            hostnames = new DisplayData();
            hostnames.apiName = 'hostnames';
            hostnames.nodeName = 'hostnames?';
            hostnames.childs = [];
            hostnames.hasChildren = true;
            hostnames.level = 0;
            hostnames.changed = false; hostnames.inserted = false; 
            hostnames.valid = null;    hostnames.deleted  = false;
            hostnames.path = hostnames.nodeName;
            this.data.push(hostnames);
        }
        // create new hostname entry in temp hostname storage for new items
        let host = new DisplayData();
        host.nodeName = '#'+(++this.insertCounter);
        host.apiName = '';
        host.changed = false; host.inserted = true; host.deleted = false; host.valid = null;
        host.level   = 1;
        host.value   = ''; host.oldValue = '';
        host.hasChildren = false;
        host.path    = hostnames.path+'.'+host.nodeName;
        hostnames.childs.push(host);
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * mark hostname as deleted, identified per path (= host url) 
     * @param path 
     */
    delHostname(path: string){
        let item = this.getItem(path); item.deleted = true; if(item.inserted!==true){ this.changed = true; } 
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * validate changes before submit data
     * throws DataValidationError
     */
    validateInsertData(): void {
        // validate tenant code
        let code = this.getItem('code');
        if(code.value.trim()===''){ code.valid=false; throw new DataValidationError(code.path, 'Code darf nicht leer sein'); }
        // validate tenant name
        let name = this.getItem('name');
        if(name.value.trim()===''){ name.valid=false; throw new DataValidationError(code.path, 'Name darf nicht leer sein'); }
        // validate new hostnames:
        let hostnames = this.getItem('hostnames?');
        if(hostnames!==null && hostnames.hasChildren){
            for(let i=hostnames.childs.length-1; i>=0; --i){
                let host = hostnames.childs[i];
                host.apiName = host.apiName.trim();
                host.value   = host.value.trim();
                // deleted or empty hostname entry -> avoid insert, just remove from output
                if(host.deleted===true || (host.apiName==='' && host.value==='')){ 
                    hostnames.childs.splice(i,1);
                }
                // empty hostname
                else if(host.apiName===''){
                    host.valid = false;
                    throw new DataValidationError(host.path, 'Hostname muss definiert werden');
                }
                // empty description (no validation required)
                /*else if(host.value===''){
                    host.valid = false;
                    throw new DataValidationError(host.path, 'Hostname-Beschreibung muss definiert werden');
                }*/
            }
        }
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * async save loop for execution multiple api-calls which execution depends in exact order
     * -> execSaveLoop calls execSaveLoop recurse
     */
    execSaveLoop(){
        // async looping of save commands:
        //  first delete hostnames specified per deleted-flag. every hostname has to be deleted separatly.
        //  after hostnames deleted, prepare update data for tenant (changed info, params, hostname-desc, +add hostnames)
        // important: every api-call can ends with an exception, in this case error must be handled and 
        //  user action is required. deleted entries (e.g. hostnames) have to be removed from temporary changed
        //  data storage because then these are deleted in database, discard deleting is not possible.
        //console.log('execSaveLoop');
        let inExec: Boolean = false;
        // check for next hostname, which marked as deleted
        inExec = this.execNextHostnameDelete(); if(inExec===true) return ;
        // check for updating tenant data, which marked as changed or inserted
        inExec = this.execTenantSave(); if(inExec===true) return ;
        // reload data
        this.loadData();
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * internal use to prepare tenant changes and submit to api
     */
    execTenantSave(): Boolean {
        let r = false;

        // first check for storable changes
        let updData = {}, changed = this.buildUpdateRecurse(this.data, updData);
        //console.log('execTenantSave changed:%o updData:%o',changed,updData);
        // exec storable changes
        if(changed===true){
            // mark as in action
            r = true;
            // add tenant id to upd-data
            updData['id']=this.id;
            // exec submit changes
            this.service.saveTenant( (updData as Tenant) ).subscribe(
                // success:
                (tenant: Tenant) => { 
                    //console.log('saveData: success tenant:%o',tenant);
                    if(this.id===null){ this.id=tenant.id; }
                    this.tenant = tenant; this.prepareDisplayData(tenant);
                },
                // error:
                (error) => { this.displayError(error); }
            );
        }
        return r;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * internal use to prepare hostname deleting and submit to api (each host separatly)
     */
    execNextHostnameDelete(): Boolean{
        let r = false;
        // get hostname-list and look for next deleted
        let hostnames = this.getItem('hostnames');
        if(hostnames!==null && hostnames.hasChildren){
            for(let i=hostnames.childs.length-1; i>=0; --i){
                let host = hostnames.childs[i];
                if(host.deleted===true){
                    //console.log('execNextHostnameDelete host:%o',host);
                    //var cfr = window.confirm('Host "'+host.apiName+'" entgültig löschen?');
                    let cfr=true;
                    if(cfr===true){
                        // mark as in action
                        r = true;
                        this.service.removeHostname(host.apiName).subscribe(
                            (response) => {
                                // delete hostname-entry from data
                                hostnames.childs.splice(i,1);
                                // go back to save-exec-loop
                                this.execSaveLoop();
                            },
                            (error) => { this.displayError(error); }
                        )
                        // break execution, deleting will be continued async
                        break;
                    }
                    // use discard deleting: reset deleted-flat
                    else { host.deleted=false; }
                }
            }
        }
        //console.log('execNextHostnameDelete r:%o',r);
        return r;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * internal usage to build request data for submit edited changes
     * @param src 
     * @param dst 
     */
    buildUpdateRecurse(src: DisplayData[], dst: object): Boolean {
        //console.log('buildUpdateRecurse src:%o dst:%o',src,dst);
        let srcTreeChanged = false;
        for(let i=0; i<src.length; ++i){
            // if value changed: add to destination
            if(src[i].changed){
                dst[src[i].apiName] = src[i].value; srcTreeChanged = true;
            }
            // if exists child-params: check params recursive for changed values
            else if(src[i].hasChildren){
                let childData = {};
                let childChanged = this.buildUpdateRecurse(src[i].childs, childData);
                //console.log(' -> check %o : %o',src[i].apiName,childChanged);
                if(childChanged){ dst[src[i].apiName] = childData; srcTreeChanged=true; }
            }
        }
        return srcTreeChanged;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * check if error is an xhr / api error and returns display message
     * -> should be outsourced
     */
    parseXhrError(err): string{
        // first use status reponse text
        let msg = err.statusText;
        // look for "_body" string parameter, which contains exception info
        if(typeof(err['_body'])==='string'){
            // transform response string in js-object
            let e = JSON.parse(err['_body']);
            // look for zend exceptions
            if(typeof(e)==='object' && typeof(e['errors'])==='object' && e.errors.length>0){
                // loop error messages
                let m = '';
                for(let i=0; i<e.errors.length; ++i){
                    // check for message in error
                    if(typeof(e.errors[i])==='object' && e.errors[i].message && typeof(e.errors[i].message)==='string'){
                        let d = e.errors[i].message.trim();
                        if(d!==''){ m += ((m!=='')?'\n':'')+d; }
                    }
                    // check for description in error
                    else if(typeof(e.errors[i])==='object' && e.errors[i].description && typeof(e.errors[i].description)==='string'){
                        let d = e.errors[i].description.trim();
                        if(d!==''){ m += ((m!=='')?'\n':'')+d; }
                    }
                }
                // extend error message with found exception message
                if(m!==''){ msg=m; }
            }
        }
        // return error text for display
        return msg;
    }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * login as sysadmin of selected tenant
     */
    loginAs(){
            // exec submit changes
            this.service.loginAs( this.tenant.code ).subscribe(
                // success: set flags and route home
                (response) => {
                    localStorage.setItem('loggedInAsSysAdmin','true');
                    this.router.navigate(['/']); 
                },
                // error:
                (error) => { this.displayError(error); }
            );
        }
    //-----------------------------------------------------------------------------------------------------------------
    /**
     * convert api-param-name in js-friendly name without special chars, using camel case
     * -> not necessary, but works
     */
    /*protected getFriendlyName(apiName: String): String {
        let name = '', upcase = false;
        for(let i=0; i<apiName.length; ++i){
            let c = apiName.charAt(i), up = upcase; upcase = false;
            if(i===0){ if(/[a-z|A-Z|_|$]/    .test(c)===false){ c = null; upcase = true; } }
            else     { if(/[0-9|a-z|A-Z|_|$]/.test(c)===false){ c = null; upcase = true; } }
            if(c!==null && up){ c = c.toUpperCase(); }
            if(c!==null){ name += c; }
        }
        return name;
    }*/
    //-----------------------------------------------------------------------------------------------------------------
}


// WEBPACK FOOTER //
// ./src/app/config/tenant/tenant.component.ts